import { cloneDeep, compact, get, uniq } from 'lodash';

// constants
import { applicationTypes, applicationProperties, scriptTypes } from 'constants/scripts';
import { UNIT_TYPES } from 'constants/unitTypes';

// Util
import { getIfCommonWith } from 'util/objectCompare';
import { copyZoneMeta } from 'util/zoneCreation';

// Selectors
import {
  getProductApplicationsForZones,
  getFertilizerApplicationsForZones
} from 'scripts/zones/fertility-selectors';
import {
  getFirstSeedApplicationsInZones,
  getSeedApplicationProductsInZones,
  getSeedApplicationsByProductInZones,
  isZoneApplyingAllSeedProducts
} from 'scripts/zones/planting-selectors';

const _createNewSeedApplication = ({
  product=null,
  targetYield={q:null, u:UNIT_TYPES.bu_ac},
  rate={q:null, u:UNIT_TYPES.seed_acre},
  estimatedYield={q:null, u:UNIT_TYPES.bu_ac},
  estimatedProfit={q:null, u:UNIT_TYPES.usd}
}) => {

  return {
    product,
    rate,
    'target-yield': targetYield,
    'estimated-yield': estimatedYield,
    'estimated-profit': estimatedProfit
  };
};

const _createNewFertilizerApplicationProperty = ({ rate={q:null, u:UNIT_TYPES.gal_ac} }) => {
  // don't create an object w/rate attribute -- just return the bare { q, u } structure
  // so it can be assigned to the correct rate property key
  return rate;
};

const _createNewFertilizerApplication = ({
  [applicationProperties.INCORPORATION_METHOD]:incorporationMethod=null,
  [applicationProperties.PRODUCT]:product=null,
  [applicationProperties.RATE]: rate,
  [applicationProperties.NITROGEN_RATE]: nitrogenRate
}) => {
  return {
    [applicationProperties.INCORPORATION_METHOD]: incorporationMethod,
    [applicationProperties.PRODUCT]: product,
    [applicationProperties.RATE]: _createNewFertilizerApplicationProperty({ rate }),
    [applicationProperties.NITROGEN_RATE]: _createNewFertilizerApplicationProperty({ rate: nitrogenRate })
  };
};

const _createNewStabilizerApplication = ({ rate={q:null, u:UNIT_TYPES.gal_ac} }) => {
  return { rate };
};

const _collectCommonSeedApplications = (zonesArray) => {
  const collectedSeedApplications = [];

  // Get list of all seed products used.
  const seedApplicationProducts = getSeedApplicationProductsInZones(zonesArray);

  // Do all zones use all of these seed products?
  const isAnyZoneNotApplyingAllProducts
    = zonesArray.some(zone => !isZoneApplyingAllSeedProducts({zone, seedApplicationProducts}) );
  if (isAnyZoneNotApplyingAllProducts) { // No--create an empty application with no product.
    collectedSeedApplications.push(_createNewSeedApplication({}));
  } else { // Yes--create applications for all products used.
    seedApplicationProducts.forEach(product => {
      collectedSeedApplications.push(_createNewSeedApplication({product}));
    });
  }

  // For each seed application product, copy across member values that are common to all zones.
  collectedSeedApplications.forEach(collectedSeedApplication => {
    // If zones didn't all use same seed products, collectedProductId will be set to null.
    const collectedProductId = get(collectedSeedApplication, 'product.id'); 

    const seedApplicationsForProduct = collectedProductId
      ? getSeedApplicationsByProductInZones({zones:zonesArray, seedApplicationProductId:collectedProductId})
      // I know this isn't right for multiple seed applications, but more UI reqs need to be defined to find an
      // approach that covers them.
      : getFirstSeedApplicationsInZones(zonesArray);
    const commonRate = getIfCommonWith(seedApplicationsForProduct, 'rate');
    const commonTargetYield = getIfCommonWith(seedApplicationsForProduct, 'target-yield');
    if (commonRate !== null) { collectedSeedApplication.rate = commonRate; }
    if (commonTargetYield !== null) { collectedSeedApplication['target-yield'] = commonTargetYield; }
  });

  return collectedSeedApplications;
};

const _collectCommonFertilizerApplications = (zones) => {
  // This code assumes just one application of fertilizer per zone,
  // and no distinction in application beyond rate and nitrogen rate.
  const applications = getFertilizerApplicationsForZones(zones);
  let newApplication = {};

  // loop through the various rates that need to be set for fertility
  [applicationProperties.RATE, applicationProperties.NITROGEN_RATE].forEach((applicationProperty) => {
    const rates = compact(applications.map(application => get(application[0], applicationProperty)));
    // don't go any farther if there are no application rates
    if (rates.length) {
      const sampleRate = cloneDeep(rates[0]) || {};
      const rateValues = uniq(rates.map(rate => get(rate, 'q')));
      // if there is more than one zone and there is more than one rate value
      // gathered from the zones, null out the new zone
      if (zones.length > 1 && rateValues.length > 1) {
        sampleRate.q = null;
      }

      newApplication[applicationProperty] = _createNewFertilizerApplicationProperty({ rate: sampleRate });
    }
  });

  // Other than rate/nitrogen rate, everything should be the same between zones, so we just use [0]
  [applicationProperties.INCORPORATION_METHOD, applicationProperties.PRODUCT].forEach( (applicationProperty) => {
    const toCopy = get(zones[0], `meta.applications.${applicationTypes.FERTILIZER}[0].${applicationProperty}`);
    newApplication[applicationProperty] = cloneDeep(toCopy);
  });
  return [newApplication];
};

const _collectCommonStabilizerApplications = (zones) => {
  // This code assumes just one application of stabilizer per zone, and no distinction in application beyond rate.
  const collectedStabilizerApplications = [_createNewStabilizerApplication({})];

  // TODO Add stabilizer once we are saving it
  [].forEach( (applicationProperty) => {
    const toCopy = get(zones[0], `meta.applications.${applicationTypes.STABILIZER}[0].${applicationProperty}`);
    collectedStabilizerApplications[0][applicationProperty] = cloneDeep(toCopy);
  });

  return collectedStabilizerApplications;
};

const _collectCommonApplicationApplications = (zones) => {
  // This code assumes just one application per zone,
  // and no distinction in application beyond rate
  const applications = getProductApplicationsForZones(zones);
  let newApplication = {};

  const rates = compact(applications.map(application => get(application[0], applicationProperties.RATE)));
  // don't go any farther if there are no application rates
  if (rates.length) {
    const sampleRate = cloneDeep(rates[0]) || {};
    const rateValues = uniq(rates.map(rate => get(rate, 'q')));
    // if there is more than one zone and there is more than one rate value
    // gathered from the zones, null out the new zone
    if (zones.length > 1 && rateValues.length > 1) {
      sampleRate.q = null;
    }

    newApplication[applicationProperties.RATE] = _createNewFertilizerApplicationProperty({ rate: sampleRate });
  }
  // });

  // Other than rate, everything should be the same between zones, so we just use [0]
  [applicationProperties.INCORPORATION_METHOD, applicationProperties.PRODUCT].forEach( (applicationProperty) => {
    const toCopy = get(zones[0], `meta.applications.${applicationTypes.APPLICATION}[0].${applicationProperty}`);
    newApplication[applicationProperty] = cloneDeep(toCopy);
  });
  return [newApplication];
};

const _collectPlantingZoneApplications = (toZone, fromZones) => {
  return {
    [applicationTypes.FERTILIZER]: _collectCommonFertilizerApplications(fromZones),
    [applicationTypes.SEED]: _collectCommonSeedApplications(fromZones)
  };
};

const _collectFertilityZoneApplications = (toZone, fromZones) => {
  return {
    [applicationTypes.FERTILIZER]: _collectCommonFertilizerApplications(fromZones),
    [applicationTypes.STABILIZER]: _collectCommonStabilizerApplications(fromZones)
  };
};

const _collectApplicationZoneApplications = (toZone, fromZones) => {
  return {
    [applicationTypes.APPLICATION]: _collectCommonApplicationApplications(fromZones)
  };
};

export const inheritZoneMetaFromMultiple = ({toZone, fromZones, scriptType}) => {
  let applications;
  switch (scriptType) {
    case scriptTypes.FERTILITY:
      applications = _collectFertilityZoneApplications(toZone, fromZones);
      break;
    case scriptTypes.APPLICATION:
      applications = _collectApplicationZoneApplications(toZone, fromZones);
      break;
    default:
      applications = _collectPlantingZoneApplications(toZone, fromZones);
  }
  const collectedZone = { meta: { applications } };
  return copyZoneMeta(toZone, collectedZone);
};

export const unitTestPrivate = {
  _collectCommonFertilizerApplications,
  _collectCommonSeedApplications,
  _collectCommonStabilizerApplications,
  _collectFertilityZoneApplications,
  _collectPlantingZoneApplications,
  _createNewFertilizerApplicationProperty,
  _createNewFertilizerApplication,
  _createNewSeedApplication,
  _createNewStabilizerApplication
};
